import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../context/authContext';
import { useAlert } from '../../../context/alertContext';
import axios from 'axios';
import { cn } from '../../../lib/utils';
import { useNavigate } from 'react-router-dom';
import { ChangePassword } from './ChangePassword';
import { IPreference } from '../../../lib/models/interfaces';

interface EditableUserFields {
  name: string;
  preferences: IPreference;
}

export const UserPreferences = () => {
  const { userM, token, getAndSetUserM } = useContext(UserContext);
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const buildInitialForm = (): EditableUserFields => {
    return {
      name: userM?.name ?? '',
      preferences: userM?.preferences
    };
  };

  const [formData, setFormData] = useState<EditableUserFields>({
    name: '',
    preferences: {
      emailNotifications: false,
      notifications: false,
      notificationTypes: {
        pii: false,
        documentComplete: false,
        documentDeletionComplete: false
      }
    }
  });

  useEffect(() => {
    if (!userM) return;
    console.log('setting form data');
    setFormData(buildInitialForm());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userM, token]);

  const handleChange = <K extends keyof EditableUserFields>(
    key: K,
    value: EditableUserFields[K]
  ): void => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handlePreferenceToggle = (
    key: keyof EditableUserFields['preferences'],
    nestedKey?: keyof IPreference['notificationTypes']
  ) => {
    setFormData((prev) => {
      if (key === 'notificationTypes') {
        if (!nestedKey) return prev;
        return {
          ...prev,
          preferences: {
            ...prev.preferences,
            notificationTypes: {
              ...prev.preferences.notificationTypes,
              [nestedKey]: !prev.preferences.notificationTypes[nestedKey]
            }
          }
        };
      }

      return {
        ...prev,
        preferences: {
          ...prev.preferences,
          [key]: !prev.preferences[key]
        }
      };
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) {
      setAlert('You must be signed in to update your profile', 'error');
      return;
    }
    try {
      setSaving(true);
      const preferences: IPreference = formData.preferences;
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/user?type=updateUserPreferences`,
        {
          name: formData.name,
          preferences
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (result.status === 200) {
        await getAndSetUserM();
      }
      setAlert('Profile updated successfully!', 'success', 3);
    } catch (error) {
      setAlert('Unable to update your profile right now.', 'error', 3);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {userM && formData ? (
        <div className="py-10 px-4">
          <div className="mx-auto max-w-4xl">
            <header className="text-center mb-10">
              <h1 className="text-3xl font-semibold text-slate-900">
                Personal Preferences
              </h1>
              <p className="mt-2 text-slate-500">
                Keep your account details up to date to help us tailor your
                experience.
              </p>
            </header>
            <div
              className="overflow-y-scroll"
              style={{ scrollbarWidth: 'none' }}
            >
              <form
                onSubmit={handleSubmit}
                className="grid gap-6 rounded-3xl border border-white/60 bg-white/90 p-8 shadow-xl backdrop-blur"
              >
                <div className="grid gap-6 md:grid-cols-2 rounded-2xl p-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]">
                  <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                    Name
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-900 shadow-sm focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200/60"
                      placeholder={`${userM.name}`}
                    />
                  </label>
                </div>

                <div className="flex flex-col gap-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)] rounded-2xl bg-slate-50/60 p-4 text-sm text-slate-600">
                  <div>
                    <div className="font-semibold text-slate-800">
                      Notifications
                    </div>
                    <p className="text-xs text-slate-500">
                      Choose how you’d like to hear from us.
                    </p>
                  </div>
                  <div className="space-y-4">
                    {[
                      {
                        key: 'emailNotifications' as const,
                        label: 'Email Notifications',
                        description:
                          'Get updates about document analysis and workflow activity'
                      },
                      {
                        key: 'notifications' as const,
                        label: 'In-app Notifications',
                        description: 'See real-time updates inside Koru AI'
                      }
                    ].map((pref) => {
                      const enabled = formData?.preferences[pref.key];
                      return (
                        <div
                          key={pref.key}
                          className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm"
                        >
                          <div>
                            <p className="text-base font-semibold text-slate-900">
                              {pref.label}
                            </p>
                            <p className="text-xs text-slate-500">
                              {pref.description}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handlePreferenceToggle(pref.key)}
                            className={cn(
                              'relative inline-flex h-7 w-14 items-center rounded-full border border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-400',
                              enabled ? 'bg-emerald-500' : 'bg-slate-300'
                            )}
                            aria-pressed={enabled}
                          >
                            <span
                              className={cn(
                                'inline-block h-6 w-6 transform rounded-full bg-white shadow transition',
                                enabled ? 'translate-x-7' : 'translate-x-1'
                              )}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex flex-col gap-4 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)] rounded-2xl bg-slate-50/60 p-4 text-sm text-slate-600">
                  <div>
                    <div className="font-semibold text-slate-800">
                      Notifications Types
                    </div>
                    <p className="text-xs text-slate-500">
                      Choose what you want to know about.
                    </p>
                  </div>
                  <div className="space-y-4">
                    {(
                      [
                        {
                          key: 'pii',
                          label: 'Sensitive Data Alerts',
                          description:
                            'Alerts when we detect potential sensitive or personal information in analyzed documents'
                        },
                        {
                          key: 'documentComplete',
                          label: 'Document Analysis Completed',
                          description: 'Alerts when analysis is finished.'
                        },
                        {
                          key: 'documentDeletionComplete',
                          label: 'Bulk Deletions Completed',
                          description:
                            'Alerts when large document batches are deleted.'
                        }
                      ] satisfies Array<{
                        key: keyof IPreference['notificationTypes'];
                        label: string;
                        description: string;
                      }>
                    ).map((pref) => {
                      const enabled =
                        formData?.preferences.notificationTypes[pref.key];
                      return (
                        <div
                          key={pref.key}
                          className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm"
                        >
                          <div>
                            <p className="text-base font-semibold text-slate-900">
                              {pref.label}
                            </p>
                            <p className="text-xs text-slate-500">
                              {pref.description}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              handlePreferenceToggle(
                                'notificationTypes',
                                pref.key
                              )
                            }
                            className={cn(
                              'relative inline-flex h-7 w-14 items-center rounded-full border border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-400',
                              enabled ? 'bg-emerald-500' : 'bg-slate-300'
                            )}
                            aria-pressed={enabled}
                          >
                            <span
                              className={cn(
                                'inline-block h-6 w-6 transform rounded-full bg-white shadow transition',
                                enabled ? 'translate-x-7' : 'translate-x-1'
                              )}
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      Stay in sync
                    </p>
                    <p className="text-sm text-slate-500">
                      These details help us personalize updates and workflows.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/app/landing')}
                      className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className={cn(
                        'rounded-full bg-emerald-500 px-6 py-2 text-sm font-semibold text-white shadow-lg transition-colors',
                        saving
                          ? 'cursor-not-allowed opacity-70'
                          : 'hover:bg-emerald-600'
                      )}
                    >
                      {saving ? 'Saving…' : 'Save Preferences'}
                    </button>
                  </div>
                </div>
              </form>
              <ChangePassword />
            </div>
          </div>
        </div>
      ) : (
        <div>No UserM</div>
      )}
    </>
  );
};
